import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Globe } from "lucide-react";
import { PerlaLogo } from "./PerlaLogo";
import { useLang } from "@/contexts/LanguageContext";
import { s } from "@/i18n/translations";

const NAV_ITEMS = [
  { to: "/", key: "nav.home" },
  { to: "/chi-siamo", key: "nav.about" },
  { to: "/servizi", key: "nav.services" },
  { to: "/testimonianze", key: "nav.testimonials" },
  { to: "/contatti", key: "nav.contacts" },
];

export const Header = () => {
  const { lang, setLang } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 border-b transition-colors ${
        scrolled
          ? "bg-[#FAFAF7]/95 backdrop-blur border-[#E5E5DF]"
          : "bg-[#FAFAF7] border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-5 lg:px-10 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="header-logo-link" aria-label="Perla Music Home">
          <PerlaLogo />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" aria-label="Main">
          {NAV_ITEMS.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-${item.to.replace("/", "") || "home"}`}
              className={({ isActive }) =>
                `text-sm font-semibold uppercase tracking-widest link-underline transition-opacity ${
                  isActive ? "text-[#FF5A1F]" : "text-[#0A0A0A]"
                }`
              }
            >
              {s(item.key, lang)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-4">
          <LangSwitcher />
          <Link to="/contatti" className="btn-cta" data-testid="header-cta-apply">
            {s("nav.apply", lang)}
          </Link>
        </div>

        <button
          type="button"
          aria-label="Menu"
          data-testid="mobile-menu-toggle"
          className="lg:hidden p-2 -mr-2 text-[#0A0A0A]"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-[#E5E5DF] bg-[#FAFAF7]" data-testid="mobile-menu">
          <div className="px-5 py-6 flex flex-col gap-1">
            {NAV_ITEMS.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`mobile-nav-${item.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `py-3 font-display text-3xl leading-none tracking-wide ${
                    isActive ? "text-[#FF5A1F]" : "text-[#0A0A0A]"
                  }`
                }
              >
                {s(item.key, lang)}
              </NavLink>
            ))}
            <div className="mt-6 flex items-center justify-between">
              <LangSwitcher />
              <Link to="/contatti" className="btn-cta" data-testid="mobile-header-cta-apply">
                {s("nav.apply", lang)}
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

const LangSwitcher = () => {
  const { lang, setLang } = useLang();
  return (
    <div
      className="flex items-center gap-1 border border-[#0A0A0A] px-2 py-1"
      data-testid="lang-switcher"
    >
      <Globe size={14} className="text-[#0A0A0A]" />
      <button
        type="button"
        onClick={() => setLang("it")}
        data-testid="lang-it"
        className={`text-xs font-bold px-1.5 ${lang === "it" ? "text-[#FF5A1F]" : "text-[#0A0A0A]/60"}`}
      >
        IT
      </button>
      <span className="text-xs text-[#0A0A0A]/30">/</span>
      <button
        type="button"
        onClick={() => setLang("en")}
        data-testid="lang-en"
        className={`text-xs font-bold px-1.5 ${lang === "en" ? "text-[#FF5A1F]" : "text-[#0A0A0A]/60"}`}
      >
        EN
      </button>
    </div>
  );
};
